import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';

import { useGoogleGeocoder } from '@common/utils/hooks/google/useGoogleGeocoder';
import {
  getLocationInformation,
  mapAddressDetailsToString,
} from '@common/utils/hooks/google/utils';

import { trimStringValues } from '../../../_common/utils/form';
import {
  DataType,
  FieldsListQuery_category_fields as Field,
} from '../../../types/api';

interface Props {
  fields: Field[];
}

export default function AddressWatcher({ fields }: Props) {
  const geocode = useGoogleGeocoder();
  const { values } = useFormState();
  const form = useForm();

  const addressFields = useMemo(
    () => fields?.filter((f) => f.dataType === DataType.ADDRESS) ?? [],
    [fields],
  );

  const geocodeAddresses = useCallback(
    async (addressFields: Field[]) => {
      try {
        await Promise.all(
          addressFields.map(async (addressField: Field) => {
            const address = JSON.parse(values[addressField.id]);

            if (
              address.location != null &&
              address.country_short != null &&
              address.timezone != null
            ) {
              return addressField;
            }

            const addressString = mapAddressDetailsToString(address, ', ');
            const result = await geocode(addressString);
            const { country_short, location, timezone } =
              (await getLocationInformation(result)) ?? {};

            const addressFormValue = JSON.stringify(
              trimStringValues({
                ...address,
                country_short,
                location,
                timezone,
              }),
            );

            form.change(addressField.id, addressFormValue);

            return addressField;
          }),
        );
      } catch (error) {
        // swallow for now
      }
    },
    [values, form, geocode],
  );

  useEffect(() => {
    geocodeAddresses(addressFields).catch(console.error);
  }, [addressFields, geocodeAddresses]);

  return null;
}
